import React, { useState, useEffect, useCallback } from 'react'
import { Modal, DatePicker, TimePicker, Input, Button, message, Icon } from 'antd'
import moment from 'moment'
import axios from '../../util/Api'

const generateTrackingUrl = (assetId, device, token) => {
    const baseUrl = window.location.origin
    const hostname = window.location.hostname

    // If localhost, use the standard path
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return `${baseUrl}/public/track/${assetId}/${device}/${token}`
    }

    // For non-localhost, add 'track' as subdomain
    const protocol = window.location.protocol
    // Remove any existing subdomain and get the main domain
    const domainParts = hostname.split('.')
    // If already has subdomain (e.g., app.domain.com), replace it; otherwise add track
    const mainDomain = domainParts.length > 2
        ? domainParts.slice(-2).join('.')
        : hostname
    return `${protocol}//track.${mainDomain}/public/track/${assetId}/${device}/${token}`
}

function ShareTrackingUrl({ assetId, device }) {
    const [shareModalVisible, setShareModalVisible] = useState(false)
    const [expirationDate, setExpirationDate] = useState(null)
    const [expirationTime, setExpirationTime] = useState(null)
    const [shareUrl, setShareUrl] = useState('')
    const [loading, setLoading] = useState(false)
    const [hasExistingUrl, setHasExistingUrl] = useState(false)
    const [deleteLoading, setDeleteLoading] = useState(false)
    const [checkingToken, setCheckingToken] = useState(true)

    const checkTokenExists = useCallback(async () => {
        if (!assetId || !device) return

        try {
            setCheckingToken(true)
            const currentTimestamp = Date.now()
            const response = await axios.get(`/live-track/check-token-exists/${assetId}/${device}/${currentTimestamp}`)
            
            if (response && response.data && response.data.token) {
                const token = response.data.token
                const url = generateTrackingUrl(assetId, device, token)
                setShareUrl(url)
                setHasExistingUrl(true)
            } else {
                setShareUrl('')
                setHasExistingUrl(false)
            }
        } catch (err) {
            console.error('Error checking token exists:', err)
            setShareUrl('')
            setHasExistingUrl(false)
        } finally {
            setCheckingToken(false)
        }
    }, [assetId, device])

    useEffect(() => {
        checkTokenExists()
    }, [checkTokenExists])

    const handleShareClick = () => {
        setShareModalVisible(true)
        if (!hasExistingUrl) {
            setExpirationDate(null)
            setExpirationTime(null)
        }
    }

    const handleGenerateUrl = async () => {
        if (!expirationDate || !expirationTime) {
            message.error('Please select both expiration date and time')
            return
        }

        const expirationMoment = expirationDate.clone()
        const timeString = expirationTime.format('HH:mm:ss')
        const [hours, minutes, seconds] = timeString.split(':').map(Number)
        expirationMoment.set({ hours, minutes, seconds })

        if (expirationMoment.isBefore(moment())) {
            message.error('Expiration date/time must be in the future')
            return
        }

        const maxDate = moment().add(30, 'days')
        if (expirationMoment.isAfter(maxDate)) {
            message.error('Expiration date/time cannot be more than 30 days from now')
            return
        }

        // Get epoch timestamp
        const expirationTimestamp = expirationMoment.valueOf()

        try {
            setLoading(true)
            setShareUrl('')

            // Call API to get token
            const response = await axios.get(`/live-track/${assetId}/${device}/${expirationTimestamp}`)
            console.log(response)
            if (response && response.data && response.data.token) {
                const token = response.data.token
                const url = generateTrackingUrl(assetId, device, token)
                setShareUrl(url)
                setHasExistingUrl(true)
                message.success('URL generated successfully!')
            } else {
                message.error('Failed to generate URL. Token not received.')
            }
        } catch (err) {
            console.error('Error generating share URL:', err)
            message.error('Failed to generate URL. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    const handleDeleteUrl = async () => {
        try {
            setDeleteLoading(true)
            const token = shareUrl.split('/').pop()
            await axios.delete(`/live-track/delete-token/${token}`)
            message.success('URL deleted successfully!')
            setShareUrl('')
            setHasExistingUrl(false)
            setExpirationDate(null)
            setExpirationTime(null)
            // Re-check token exists after deletion
            await checkTokenExists()
        } catch (err) {
            console.error('Error deleting share URL:', err)
            message.error('Failed to delete URL. Please try again.')
        } finally {
            setDeleteLoading(false)
        }
    }

    const handleCopyUrl = async () => {
        if (!shareUrl) {
            message.warning('Please generate URL first')
            return
        }

        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(shareUrl)
                message.success('URL copied to clipboard!')
            } else {
                // Fallback for older browsers
                const input = document.createElement('input')
                input.value = shareUrl
                input.style.position = 'fixed'
                input.style.opacity = '0'
                document.body.appendChild(input)
                input.select()
                document.execCommand('copy')
                document.body.removeChild(input)
                message.success('URL copied to clipboard!')
            }
        } catch (err) {
            console.error('Failed to copy URL:', err)
            message.error('Failed to copy URL')
        }
    }

    return (
        <>
            <Icon
                type="share-alt"
                style={{ 
                    fontSize: '18px', 
                    cursor: 'pointer', 
                    color: hasExistingUrl ? '#f5222d' : '#1890ff' 
                }}
                onClick={handleShareClick}
            />
            <Modal
                title="Share Tracking URL"
                visible={shareModalVisible}
                onCancel={() => setShareModalVisible(false)}
                footer={null}
                width={500}
            >
                {!hasExistingUrl && (
                    <>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '8px' }}>Expiration Date:</label>
                            <DatePicker
                                style={{ width: '100%', marginBottom: '16px' }}
                                value={expirationDate}
                                onChange={setExpirationDate}
                                format="YYYY-MM-DD"
                                disabledDate={(current) => {
                                    if (!current) return false
                                    const today = moment().startOf('day')
                                    const maxDate = moment().add(30, 'days').endOf('day')
                                    return current < today || current > maxDate
                                }}
                            />
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '8px' }}>Expiration Time:</label>
                            <TimePicker
                                style={{ width: '100%', marginBottom: '16px' }}
                                value={expirationTime}
                                onChange={setExpirationTime}
                                format="HH:mm:ss"
                            />
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <Button
                                type="primary"
                                onClick={handleGenerateUrl}
                                style={{ width: '100%' }}
                                loading={loading}
                                disabled={loading || checkingToken}
                            >
                                Generate URL
                            </Button>
                        </div>
                    </>
                )}
                {hasExistingUrl && (
                    <div style={{ marginBottom: '16px' }}>
                        <Button
                            type="danger"
                            onClick={handleDeleteUrl}
                            style={{ width: '100%' }}
                            loading={deleteLoading}
                            disabled={deleteLoading}
                        >
                            Delete URL
                        </Button>
                    </div>
                )}
                {shareUrl && (
                    <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', marginBottom: '8px' }}>Shareable URL:</label>
                        <Input.Group compact>
                            <Input
                                value={shareUrl}
                                readOnly
                                style={{ width: 'calc(100% - 40px)' }}
                            />
                            <Button
                                icon="copy"
                                onClick={handleCopyUrl}
                                style={{ width: '40px' }}
                            />
                        </Input.Group>
                    </div>
                )}
            </Modal>
        </>
    )
}

export default ShareTrackingUrl

